/**
 * Display formatting for `ColocaAmountInput` when thousands grouping is on.
 *
 * ## The one invariant
 *
 * **The stored value never changes shape.** It stays the plain dot-decimal string the API receives
 * (`"1500.50"`); grouping is a view rendered on top of it and parsed straight back. Nothing downstream — the
 * quote request, `parseAmountInputDecimal`, the funds check, the payout payload — sees a grouped string.
 *
 * That separation is the whole point. Keeping one string for both jobs, which is what the component does
 * ungrouped, would be silently catastrophic here: `parseAmountInputDecimal` only reads dots as thousands
 * marks when a comma is also present, so a displayed-and-stored `"1.500"` travels to the backend as **1.5**.
 * A payout of one and a half pesos instead of fifteen hundred, with no error anywhere.
 *
 * ## Why not `Intl.NumberFormat` for the whole number
 *
 * Two reasons it cannot format a half-typed amount. Its `maximumFractionDigits` defaults to 3, which would
 * silently eat the 8-dp precision the ledger works in; and it normalizes, so `"1500.5"` renders as
 * `"1.500,5"` only by luck and `"1500."` loses the separator the user just pressed. Intl is used for one
 * thing only: asking the locale which characters it groups and points with.
 */

export interface AmountSeparators {
  /** Thousands mark — `"."` in es-CO. */
  group: string;
  /** Decimal mark — `","` in es-CO. */
  decimal: string;
}

const SEPARATORS_BY_LOCALE = new Map<string, AmountSeparators>();

/** The locale's grouping and decimal characters, memoized — `formatToParts` is not cheap per keystroke. */
export function separatorsFor(locale: string): AmountSeparators {
  const cached = SEPARATORS_BY_LOCALE.get(locale);
  if (cached) return cached;

  let separators: AmountSeparators = { group: ".", decimal: "," };
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    separators = {
      group: parts.find((p) => p.type === "group")?.value ?? ".",
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ",",
    };
  } catch {
    // Keep the es-CO fallback: an unknown locale must not take the field down.
  }

  SEPARATORS_BY_LOCALE.set(locale, separators);
  return separators;
}

/**
 * Canonical dot-decimal string → what the field shows.
 *
 * Digits are never added or removed: `"1500.5"` becomes `"1.500,5"`, not `"1.500,50"`. A trailing separator
 * survives (`"1500."` → `"1.500,"`) because the user pressed it and is about to type the cents.
 */
export function formatGroupedAmount(canonical: string, separators: AmountSeparators): string {
  if (!canonical) return "";

  const dot = canonical.indexOf(".");
  const integerDigits = dot === -1 ? canonical : canonical.slice(0, dot);
  const fraction = dot === -1 ? null : canonical.slice(dot + 1);

  // Insert the mark at every position that has a multiple of three digits to its right.
  const grouped = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, separators.group);

  return fraction === null ? grouped : `${grouped}${separators.decimal}${fraction}`;
}

/**
 * What the user's edit means, which the text alone cannot always say.
 *
 * `"1.5000"` is the only honest example: it is what the field holds both after appending a `0` to a grouped
 * `1.500` (fifteen thousand) and after pasting one-point-five-thousandths. Identical strings, amounts a
 * factor of ten thousand apart. The edit that produced it is the only thing that separates them, so it is
 * passed in rather than guessed.
 *
 * **Every field here is mandatory, and none of them has a default.** Each of the three money bugs that
 * reached review was the parser acting on a fact nobody had stated: once from a `decimalJustTyped` with no
 * position, twice from an edit whose source was read out of an ABSENT `pasted`. An optional boolean lets a
 * caller stay silent and still get an answer, and the answer silence bought was always the keyboard one —
 * which is the wrong reading for the one thing a new caller is likely to want (normalising a string that
 * arrived from an API, a URL or a clipboard). So the shape refuses silence: the discriminant `edit` must be
 * stated, and `separatorAt` lives INSIDE the `keystroke` arm, so the reading that needs a position cannot be
 * constructed without one.
 *
 * **Do not hand-build this from a DOM event — call `amountEditIntent`.** The one fact that tells the two arms
 * apart is how many characters the edit inserted, and on a paste some engines report them and some report
 * nothing (see `AmountEditEvent.data`). The sixth money bug was the change in LENGTH standing in for that
 * count where it was missing, which stops being the same number the moment a paste replaces a selection:
 * `"1,234.56,"` dropped over a selected `"1.500,50"` grows the field by one, came back as a decimal point
 * typed at the end, and stored 1.23456 instead of 1234.56. The run is reconstructed from the text now, and
 * that is also where the single character that may be a decimal point comes from — never the caret probe that
 * used to answer for it.
 */
export type AmountEditIntent =
  | {
      /** One character arrived at a caret — a keypress, or a one-character clipboard insertion. */
      edit: "keystroke";
      /**
       * WHERE the separator landed, or `null` when the character was a digit (or this was a deletion).
       *
       * Carrying the position is the whole point. Saying a separator was typed without saying where is the
       * shape of two separate order-of-magnitude bugs: the parser cannot find it again, because grouping puts
       * marks on BOTH sides of the one that was pressed, so `indexOf` finds the leftmost and `lastIndexOf`
       * the rightmost and neither is it. Pressing `","` at the start of `250.000,75` stored `.25000075`;
       * pressing `"."` into the middle of `1.500.000` stored `1500.000`, one and a half million read as
       * fifteen hundred. Both from one keystroke, both silent.
       */
      separatorAt: number | null;
    }
  | {
      /**
       * The text arrived whole — a paste, a drop, an autofill, an Android word insertion — **or it was never
       * an edit at all**: a value being normalised out of an API response, a URL or a deep link.
       *
       * That third case is why this is a named source rather than a `bulk?: boolean`. An optional flag let a
       * caller stay silent, and silence resolved to the keyboard reading, which is the wrong one for exactly
       * that caller: `parseGroupedAmount("1,234.56", sep, 2)` returned 1.23 instead of 1234.56. There is no
       * default and no optional field here, so the question cannot go unanswered.
       */
      edit: "bulk";
    };

/**
 * The raw facts of one edit, as the DOM reports them.
 *
 * Kept separate from `AmountEditIntent` so that deriving one from the other is a pure function with tests of
 * its own. Every money bug that was not the parser's has lived in that derivation — the Backspace read as a
 * typed decimal point, and the sixth, a paste miscounted as a single character — which is why nineteen tests
 * of the parser could not see the first of them.
 */
export interface AmountEditEvent {
  /** `InputEvent.inputType`, or `""` where the platform reports none. */
  inputType: string;
  /**
   * `InputEvent.data` — the characters that arrived, or `null` where the engine reports none.
   *
   * **Which one you get on a paste is engine-dependent, and this field used to claim otherwise.** Input
   * Events Level 2 answers "null, and the text travels in `dataTransfer`" — and that is what a
   * `contenteditable` sees. A plain `<input>` is not that: Chromium 147 puts the whole pasted string in
   * `data` and leaves `dataTransfer` NULL, on `beforeinput` and on `input` alike. Measured, not read off the
   * spec. Firefox and WebKit were not measured here, so neither answer can be assumed.
   *
   * Nothing downstream has to care, because `amountEditIntent` reads this first and reconstructs the run from
   * the text when it is null. Both halves have to be right: the engines that populate it are served by the
   * first, and the sixth money bug lived in what used to stand in for the second.
   */
  data: string | null;
  /** The field's text after the edit. */
  raw: string;
  /** What the field showed before it. */
  displayed: string;
  /** Caret offset after the edit. */
  caret: number;
}

/**
 * The run of characters an insertion put into the field, recovered from the text alone.
 *
 * Only reached when `data` came back null, which on a paste is an engine's choice rather than a rule — see
 * `AmountEditEvent.data`. What used to stand in for it there was the change in LENGTH, a different quantity
 * entirely the moment the insertion replaces a selection, which is how most people replace an amount. Pasting
 * `"1,234.56,"` over the eight characters of a selected `"1.500,50"` grows the field by exactly one, so the
 * count claimed a single character had arrived, the probe behind the caret read the `","` that merely ENDS
 * the pasted text, and the whole paste was handed to the keystroke rules as a decimal point pressed at the
 * end: **`1.23456` stored where `1234.56` belonged**, three orders of magnitude down. It is the sixth money
 * bug, and the same text in the same mis-reading that an earlier round already closed for a paste into an
 * EMPTY field — reopened by the one display length where the arithmetic happens to line up, and invisible to
 * the test that closed it, which pasted into nothing and so never grew by one.
 *
 * An insertion only ever rewrites one contiguous run, so the run is recoverable: everything after the caret
 * is the untouched tail of what was on screen, everything the two strings still share at the front is the
 * untouched head, and what lies between them is what arrived. The tail is measured FIRST and caps the head
 * scan, because a character the tail already accounts for would otherwise be counted as shared as well and
 * the run would come back one character short — the same mis-reading wearing a different hat. Pasting `"1"`
 * at the start of `"1.500"` is where that shows: the pasted character is also the first character on screen,
 * so without the cap the edit reconstructs to nothing and a paste reads as a deletion.
 *
 * **Deliberately not read from `dataTransfer`.** It is the obvious place to look and it is the wrong one: on
 * the `input` event of a plain `<input>`, Chromium 147 leaves it null and puts the text in `data` instead —
 * measured, in the engine this dashboard is tested in. A fix resting on it would therefore not even run
 * there, and would hold in whichever browser it had been tried in and nowhere else. Nothing is lost by
 * ignoring it, either: an engine that reports the text at all reports it in `data`, which the `??` at the
 * call site already takes, and the text on screen says the same thing in every one of them.
 */
function insertedTextAt(raw: string, displayed: string, caret: number): string {
  // Everything from the caret on survived untouched, so its length says how much of the old text the front of
  // the string still has to account for.
  const tail = raw.length - caret;
  // No single insertion can leave behind more text than there was to keep, so a caret saying otherwise is not
  // describing one contiguous edit. Claim everything up to it: an over-long run can only send the edit to the
  // bulk rules, which never read a separator as a decimal point, and that is the safe direction to be wrong in.
  if (tail < 0 || tail > displayed.length) return raw.slice(0, Math.max(caret, 0));
  let head = 0;
  const limit = Math.min(caret, displayed.length - tail);
  while (head < limit && raw[head] === displayed[head]) head++;
  return raw.slice(head, caret);
}

/**
 * What an edit meant, from what the browser says about it.
 *
 * **`inputType` is the only thing that says what the edit WAS**, and reading the character behind the caret
 * instead is the bug this function exists to close: after a Backspace that character is whatever the deletion
 * exposed, and grouping is inserted as the user types, so it is very often a group mark. Deleting the `5`
 * from a displayed `1.500` left `1.00` with the caret at 2 — a group mark right behind it — which rule 3
 * below read as a decimal point and stored as **one** instead of a hundred. A payout two orders of magnitude
 * off, from pressing Backspace.
 *
 * The bulk/keystroke split comes from the same signal for the same reason: inferring it from a jump in length
 * missed a paste over a selection of a similar size, which is how most people replace an amount.
 */
export function amountEditIntent(event: AmountEditEvent, separators: AmountSeparators): AmountEditIntent {
  const { inputType, data, raw, displayed, caret } = event;
  const isSeparator = (char: string) => char === separators.group || char === separators.decimal;

  // A deletion, a cut, an undo: nothing was typed, so no separator was meant as a decimal point. With no
  // `inputType` at all — an older engine, some IMEs, a synthetic event — the run reconstructed below is the
  // only thing left that can tell an insertion from a deletion, and a deletion reconstructs to nothing: the
  // head runs all the way up to the caret and the tail accounts for everything after it.
  if (inputType && !inputType.startsWith("insert")) return { edit: "keystroke", separatorAt: null };

  // What this edit inserted. The engine reports it in `data` where it reports it at all, and on a paste that
  // is not guaranteed — so the run is reconstructed from the text when it is missing. See `insertedTextAt`
  // for the quantity that used to stand in here and what it cost.
  const insertedText = data ?? insertedTextAt(raw, displayed, caret);
  const insertedCount = insertedText.length;

  // Bulk — a paste, a drop, an **autofill**, an Android word insertion, `execCommand("insertText")`. Naming
  // only the paste/drop inputTypes let every other one fall through to the keystroke rules, where the
  // grouping assumptions do not hold: an autofilled "1500.50" stored 150050, a hundred times the amount.
  //
  // **The inputType only gets a vote when the engine did not say what arrived.** `data` is a count rather
  // than a guess, so a clipboard that delivered ONE character describes a single character landing in text
  // that was already on screen — the keystroke rules' own situation, whoever put it there. Letting the
  // inputType outrank it sent a one-digit paste to the paste rules, which re-read the grouping from scratch:
  // pasting `"0"` onto a displayed `1.500` stored **1.5**, where typing the same `0` at the same caret stored
  // 15000. Four orders of magnitude between two edits that leave the field showing the identical text.
  //
  // With `data` absent the count is reconstructed, and a paste whose text happens to share a prefix with what
  // it replaced can still come back as one character — so there the inputType stays the authority and the
  // edit is read as bulk, which is the direction that never mistakes a separator for a decimal point.
  const reconstructedRun = data === null;
  const pasteOrDrop = inputType === "insertFromPaste" || inputType === "insertFromDrop";
  const bulk = insertedCount > 1 || (pasteOrDrop && reconstructedRun);

  // Only a SINGLE inserted character can be a decimal point, and when exactly one arrived the text IS what was
  // on screen with that character spliced in at the caret — which is the keystroke rules' own situation,
  // whoever put it there. A one-character paste of a separator is still somebody reaching for a decimal point,
  // so this runs before `bulk` on purpose. The cost of saying so is that such a paste is indistinguishable
  // from typing, and it stays that way: the two produce the same `raw`, the same `caret` and the same text on
  // screen, so nothing short of the clipboard itself could separate them — and they mean the same thing.
  const inserted = insertedCount === 1 ? insertedText : "";
  if (isSeparator(inserted)) return { edit: "keystroke", separatorAt: caret - 1 };
  return bulk ? { edit: "bulk" } : { edit: "keystroke", separatorAt: null };
}

/**
 * What the field shows → canonical dot-decimal string.
 *
 * Deciding which separator is the decimal point is the whole difficulty, because grouping is inserted as the
 * user types, so the text always carries marks nobody pressed. The rules, in order:
 *
 * 0. **A keystroke never produces mixed notation.** If the text already carries the locale's decimal mark, that
 *    mark — the one that was ALREADY there, not merely the leftmost — is the decimal point, and any separator
 *    typed on top of it is a typo. Only a paste can legitimately arrive in the other notation, so rule 1 is
 *    reachable only from one.
 * 1. **Both kinds present** — the kind that occurs exactly once, last, is the decimal point. A pasted
 *    `"1,234.56"` and a pasted `"1.234,56"` both read as 1234.56, whatever the locale.
 * 2. **Only the decimal kind** — it is the decimal point. Typing `","` in es-CO always means cents.
 * 3. **A separator was just typed** — it is the decimal point, whichever kind it is, and it points WHERE IT WAS
 *    PRESSED (`decimalAt`). This is what lets somebody press `.` out of habit: the next render turns it into
 *    `","`, so it never survives to confuse the keystroke after it.
 * 4. **Pasted text with a single group mark** — grouping when it cuts a whole group on BOTH sides (one to three
 *    digits before, no leading zero; exactly three after), a decimal point otherwise. That is what reads
 *    `"1.500"` as fifteen hundred, `"1500.50"` as fifteen hundred and fifty cents, and `"0.500"` as half a
 *    unit.
 * 5. **Anything else** — every group mark is grouping. This is the ordinary keystroke path, and it is what makes
 *    appending a digit to `"1.500"` yield fifteen thousand instead of one and a half.
 *
 * Rules 0 and 2 both yield to one exception, `groupsCleanly`: a mark that occurs more than once and still
 * cuts whole groups is grouping, because nothing points twice. Without it `"1,234,567"` read as 1.234567.
 */
export function parseGroupedAmount(
  text: string,
  separators: AmountSeparators,
  maxFractionDigits: number,
  intent: AmountEditIntent,
): string {
  const cleaned = text.replace(/[^\d.,]/g, "");
  if (!cleaned) return "";

  // Bulk text that was not a deliberate separator press is COMPLETE, so a mark at its end is noise — never
  // somebody about to type cents. Left in, it made rule 1 see a repeated comma in "1,234.56," and read the
  // whole thing as grouping: 123456 instead of 1234.56. Stripping from the end cannot shift `decimalAt`,
  // and this branch is the one where there is no `decimalAt` to shift.
  const meaningful = intent.edit === "bulk" ? cleaned.replace(/[.,]+$/, "") : cleaned;
  if (!meaningful) return "";

  const [integerPart, fractionPart] = splitGrouped(meaningful, separators, intent);
  // Leading zeros carry no value, and they have to go BEFORE the cap. The cap keeps the TWELVE LEFTMOST
  // digits, so a zero-padded figure out of a fixed-width export spent them all on padding and lost the real
  // ones off the end: "00000000001500" stored 15, and "000000000000001500" stored 000000000000 — a zero
  // amount that `MONEY_INPUT_PATTERN` happily accepts. Stripping first is also what keeps the display
  // honest, since "00123" otherwise renders as "00.123".
  const integerDigits = integerPart.replace(/^0+(?=\d)/, "").slice(0, 12);
  if (fractionPart == null || maxFractionDigits === 0) return integerDigits;
  return `${integerDigits}.${fractionPart.slice(0, maxFractionDigits)}`;
}

const digitsOnly = (text: string) => text.replace(/[.,]/g, "");

/** Split at `index`, keeping only digits on each side. */
function splitAt(value: string, index: number): [string, string] {
  return [digitsOnly(value.slice(0, index)), digitsOnly(value.slice(index + 1))];
}

/** The first `mark` in `value` that is not the one at `excluded` — i.e. the one that was already there. */
function indexOfExcluding(value: string, mark: string, excluded: number | null): number {
  for (let at = value.indexOf(mark); at !== -1; at = value.indexOf(mark, at + 1)) {
    if (at !== excluded) return at;
  }
  return -1;
}

/**
 * Does every mark of this kind sit exactly where grouping would put one?
 *
 * **Nothing points twice.** A mark that occurs more than once cannot be the decimal point, so when the text
 * still reads as well-formed grouping, grouping is what it is: `"1,234,567"` is a million whatever the locale
 * happens to call the comma.
 *
 * This is the one check the field had BEFORE thousands grouping existed, and losing it is the whole of the
 * fourth money bug: rule 2 claims the locale's own decimal mark unconditionally, so a pasted `"1,234,567"`
 * stored `1.234567` — six orders of magnitude down, on the most ordinary paste there is, a figure copied out
 * of a spreadsheet in en-US notation. Mirrored exactly for a `"1.234.567"` pasted into an en-US field.
 *
 * More than one occurrence is required on purpose. With exactly one the mark is genuinely ambiguous and the
 * locale is the only thing that can decide, which is rule 2's actual job: a pasted `"1,234"` in es-CO is
 * one-point-two-three-four, and must stay that way.
 */
function groupsCleanly(value: string, mark: string): boolean {
  const parts = value.split(mark);
  // A trailing mark is a decimal point the user is about to type cents after, never a group, so it does not
  // get to vote on whether the rest groups. Dropping it is also what keeps `"1,234,"` ambiguous — two parts
  // once the empty one is gone, so the locale still decides it is 1.234 and not 1234.
  if (parts.length > 1 && parts[parts.length - 1] === "") parts.pop();
  if (parts.length <= 2) return false;
  // A first group runs one to three digits and never starts with a zero — the same shape rule 4 applies to a
  // single mark, and what keeps a malformed `"0,500,000"` from being read as half a million.
  if (!/^[1-9]\d{0,2}$/.test(parts[0])) return false;
  // Every later group is exactly three digits. The last may carry a fraction, because the OTHER kind can
  // still be pointing inside it — `"1,234,567.89"` groups cleanly on the comma and points with the dot.
  return parts
    .slice(1)
    .every((part, index, rest) =>
      index === rest.length - 1 ? /^\d{3}([.,]\d+)?$/.test(part) : /^\d{3}$/.test(part),
    );
}

/** Integer digits and fraction digits; a null fraction means no decimal point survived. */
function splitGrouped(
  value: string,
  separators: AmountSeparators,
  intent: AmountEditIntent,
): [string, string | null] {
  // Where the user actually pressed. Null when this edit inserted no separator.
  const decimalAt = intent.edit === "keystroke" ? intent.separatorAt : null;
  // Whether the keystroke rules apply — a read of a STATED field, never an inference from a missing one.
  const typedIn = intent.edit === "keystroke";

  // Rule 0 — a separator pressed on top of an amount that already points somewhere is a slip of the finger,
  // never a second decimal point. Without this, rule 1 answered a keystroke it knows nothing about: with
  // `1,5` on screen, pressing "." stored **15**, and on `1.500,2` either key stored **15002** — the decimal
  // point did not move, it DISAPPEARED. Pressing the decimal key twice is the likeliest slip of all, so the
  // most consequential case was also the most reachable.
  //
  // **The mark that was ALREADY pointing wins**, and the one just pressed is the slip. Not "the first mark":
  // `indexOf` returns the LEFTMOST, which is the pre-existing one only while the slip lands to its right.
  // Press the decimal key anywhere to its left and the decimal point moved to the caret instead of the
  // keystroke being dropped — `250.000,75` with "," at Home stored `.25000075`.
  // `groupsCleanly` asks whether the text reads as well-formed grouping — a question about the text that was
  // ALREADY on screen. The mark just pressed is not part of that text, and leaving it in is the fifth money
  // bug: on a keystroke the repetition is already EXPLAINED (`separatorAt` says which mark is new), so it is
  // a slip and never grouping, but with the slip counted in, a displayed `1,123456` with "," pressed after
  // the `456` read as the well-formed `1,123,456` and stored 1123.456 — a thousand times the amount. It also
  // made the two separator keys disagree at that caret, since only one of them trips the heuristic.
  const preExisting = decimalAt == null ? value : value.slice(0, decimalAt) + value.slice(decimalAt + 1);
  if (typedIn && value.includes(separators.decimal) && !groupsCleanly(preExisting, separators.decimal)) {
    const alreadyPointing = indexOfExcluding(value, separators.decimal, decimalAt);
    if (alreadyPointing !== -1) return splitAt(value, alreadyPointing);
    return splitAt(value, decimalAt ?? value.indexOf(separators.decimal));
  }

  const hasDot = value.includes(".");
  const hasComma = value.includes(",");

  // Rule 1 — mixed notation, which only a paste can carry (rule 0 claimed every keystroke).
  if (hasDot && hasComma) {
    const lastSeparator = Math.max(value.lastIndexOf("."), value.lastIndexOf(","));
    const kind = value[lastSeparator];
    if (value.indexOf(kind) === lastSeparator) return splitAt(value, lastSeparator);
    return [digitsOnly(value), null];
  }

  const kind = hasDot ? "." : hasComma ? "," : null;
  if (kind == null) return [value, null];

  // Rule 2 — the locale's own decimal mark is never grouping, UNLESS it occurs more than once and still cuts
  // whole groups, which no decimal point can. Reachable only from a paste now: rule 0 says the same thing for
  // anything typed, and says it before mixed notation gets a vote.
  if (kind === separators.decimal && !groupsCleanly(value, kind)) {
    return splitAt(value, value.indexOf(kind));
  }

  // Rule 3 — the mark the user just pressed, whichever kind it is, AT THE POSITION THEY PRESSED IT.
  // `lastIndexOf` found the rightmost GROUP mark of the rendered figure, which bears no relation to the key:
  // pressing "." after the "1.5" of a displayed `1.500.000` put the decimal point before the last group and
  // stored 1500.000 — fifteen hundred, from one and a half million. It also made the two separator keys
  // answer differently at the same caret, which this rule's own "whichever kind it is" forbids.
  if (decimalAt != null) return splitAt(value, decimalAt);

  // Rule 4 — pasted text, where no keystroke says what was meant.
  const occurrences = value.split(kind).length - 1;
  if (intent.edit === "bulk" && occurrences === 1) {
    const index = value.indexOf(kind);
    const digitsAfter = value.length - index - 1;
    // What precedes the mark has to look like a first group too: one to three digits, never a leading zero.
    // Reading only the three digits AFTER it made `0.500` five hundred — a pasted sub-unit amount multiplied
    // by a thousand, silently. `1500.500` is not grouped either; grouped, that figure reads `1.500.500`.
    const groupedShape = digitsAfter === 3 && /^[1-9]\d{0,2}$/.test(value.slice(0, index));
    if (!groupedShape) return splitAt(value, index);
  }

  // Rule 5 — grouping.
  return [digitsOnly(value), null];
}

/**
 * Digits (and the decimal point) to the left of the caret — the only thing worth preserving across a
 * reformat.
 *
 * Counting characters instead would move the caret every time a group mark is inserted or dropped, which is
 * the classic symptom of a money field: you type the fourth digit and the cursor jumps a place left.
 */
export function countSignificantBefore(text: string, caret: number, separators: AmountSeparators): number {
  let count = 0;
  for (let i = 0; i < Math.min(caret, text.length); i++) {
    const char = text[i];
    if ((char >= "0" && char <= "9") || char === separators.decimal) count++;
  }
  return count;
}

/** The caret offset in `formatted` that sits after `significant` digits — the inverse of the count above. */
export function caretAfterSignificant(
  formatted: string,
  significant: number,
  separators: AmountSeparators,
): number {
  if (significant <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    const char = formatted[i];
    if ((char >= "0" && char <= "9") || char === separators.decimal) seen++;
    if (seen === significant) return i + 1;
  }
  return formatted.length;
}
